import * as authService from "@mirage/service-auth";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { isDropboxerAccount } from "@mirage/shared/account";
import {
  exposeSurfaceSentryInstance,
  SENTRY_IGNORE_ERRORS,
} from "@mirage/shared/sentry";
import { initSentryReplay } from "@mirage/shared/sentryReplay";
import { isEsvConfig } from "@mirage/shared/util/esv";
import { isDevBuildChannel, isIframe } from "@mirage/shared/util/tiny-utils";
import {
  getExtensionChannel,
  getExtensionVersion,
  hasMinExtensionVersion,
  isExtensionInstalled,
} from "@mirage/shared/util/webExtensionsHelpers";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { getIsDfbUser } from "../../shared/eligibility";
import { DBID_KEY, setURLParamInLocalStorage, UID_KEY } from "./readUrlParam";

const SENTRY_WEB_DSN =
  "https://46375641387b571221bdb2569b66d47d@o4506146950676480.ingest.sentry.io/4506634449715200";
const SENTRY_ESV_DSN =
  "https://799c17e603160fe4489630c214a9ac55@o4506146950676480.ingest.us.sentry.io/4509577175957505";

const LOGIN_SYNC_V2_MIN_SCOUT_VERSION = "5.210.0";
const EXTENSION_SETTINGS_MIN_SCOUT_VERSION = "5.346.0";
const EXTENSION_TAB_INGESTION_MIN_SCOUT_VERSION = "5.373.0";

export function expectLoginSyncV2() {
  return hasMinExtensionVersion(LOGIN_SYNC_V2_MIN_SCOUT_VERSION);
}

export function expectExtensionSettings() {
  return hasMinExtensionVersion(EXTENSION_SETTINGS_MIN_SCOUT_VERSION);
}

export function expectExtensionTabIngestion() {
  return hasMinExtensionVersion(EXTENSION_TAB_INGESTION_MIN_SCOUT_VERSION);
}

export async function start() {
  Sentry.init({
    dsn: isEsvConfig() ? SENTRY_ESV_DSN : SENTRY_WEB_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 1.0,
    environment: EnvCtx.buildChannel,
    replaysSessionSampleRate: 0.1,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
    release: EnvCtx.version,
    ignoreErrors: [
      ...SENTRY_IGNORE_ERRORS.NETWORK,
      ...SENTRY_IGNORE_ERRORS.MIRAGE,
    ],
    enabled: !isDevBuildChannel(EnvCtx.buildChannel),
  });

  exposeSurfaceSentryInstance(Sentry);

  Sentry.setTag("dash_app", "web");
  Sentry.setTag("install_id", await authService.getInstallId());

  Sentry.setUser({ ip_address: "{{auto}}" }); // Set IP address for investigating Sev.

  // `?dbid=<dbx-account-id>` can be passed into the webapp.
  // This is useful to identify the user before login.
  const dbid = setURLParamInLocalStorage(DBID_KEY);
  const uid = localStorage.getItem(UID_KEY);
  if (dbid || uid) {
    Sentry.setUser({ id: uid ? Number(uid) : undefined, dbid });
  }

  authService.listenForAccount().subscribe(async (account) => {
    if (!account) {
      return;
    }

    localStorage.setItem(DBID_KEY, account.account_id);
    localStorage.setItem(UID_KEY, String(account.uid));

    Sentry.setUser({
      id: account.uid,
      dbid: account.account_id,
    });

    initSentryReplay(EnvCtx.buildChannel === "development");

    Sentry.setTag("isExtensionInstalled", isIframe() || isExtensionInstalled());
    Sentry.setTag("extensionVersion", getExtensionVersion());
    Sentry.setTag("extensionBuildChannel", getExtensionChannel());
    Sentry.setTag("isDropboxer", isDropboxerAccount(account));
    const isDfbUser = await getIsDfbUser();
    Sentry.setTag("isDfbUser", isDfbUser);
  });
}

//
// Use Sentry.captureException(err); to report a caught error
// Use Sentry.captureMessage("Something went wrong"); to report any message
